import { useMemo } from 'react';
import { Eye, EyeOff, RotateCcw } from 'lucide-react';
import { DISPLAY_REGISTRY } from '../../config/displayRegistry';

/**
 * Editor for the display customization overrides of a single category. Parent
 * owns the current overrides map and the save button; this component just
 * renders inputs + toggles for one category's entries. Multiple instances
 * are stacked in the admin UI, one per category — the sidebar TOC anchors
 * to each instance so the page grows without becoming a search-in-a-modal.
 *
 * Props:
 *   category — the registry category to render (e.g. "Agreement Detail")
 *   overrides — full overrides map at this level: { [key]: { visible, label } }
 *     Only entries whose keys belong to `category` are actually shown/edited;
 *     but the parent passes the whole map so unrelated entries survive the
 *     round-trip through onChange.
 *   onChange(nextOverrides) — full replacement overrides map
 *   inheritedOverrides — overrides from the level above; used as placeholder
 *     text and for "cleared row" fallback semantics. Optional.
 *   showInheritColumn — cosmetic label for the reset button ("Use inherited"
 *     on the company page vs "Reset to default" on the global page).
 */
export default function DisplayOverridesEditor({
  category,
  overrides = {},
  onChange,
  inheritedOverrides = {},
  showInheritColumn = false,
}) {
  const sectionGroups = useMemo(() => {
    const entries = DISPLAY_REGISTRY.filter(e => e.category === category);
    const groups = new Map();
    for (const entry of entries) {
      if (!groups.has(entry.section)) groups.set(entry.section, []);
      groups.get(entry.section).push(entry);
    }
    return Array.from(groups.entries());
  }, [category]);

  const setKeyField = (key, field, value) => {
    const current = overrides[key] || {};
    const nextEntry = { ...current, [field]: value };
    // Prune the key entirely if both fields fall back to inherit.
    const isEmpty = nextEntry.visible == null && (nextEntry.label == null || nextEntry.label === '');
    const { [key]: _drop, ...rest } = overrides;
    onChange(isEmpty ? rest : { ...rest, [key]: nextEntry });
  };

  const clearKey = (key) => {
    const { [key]: _drop, ...rest } = overrides;
    onChange(rest);
  };

  const effectiveVisible = (key) => {
    const own = overrides[key]?.visible;
    if (own != null) return own;
    const inherited = inheritedOverrides[key]?.visible;
    if (inherited != null) return inherited;
    const registryEntry = DISPLAY_REGISTRY.find(e => e.key === key);
    return registryEntry?.defaultVisible ?? true;
  };

  const inheritedLabel = (key) => {
    const inherited = inheritedOverrides[key]?.label;
    if (inherited) return inherited;
    const registryEntry = DISPLAY_REGISTRY.find(e => e.key === key);
    return registryEntry?.defaultLabel || '';
  };

  if (sectionGroups.length === 0) {
    return (
      <div className="p-4 text-sm text-neutral-500 dark:text-neutral-400 italic">
        No customizable elements registered for {category}.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {sectionGroups.map(([sectionName, sectionEntries]) => (
        <div key={sectionName} className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
          <div className="bg-neutral-50 dark:bg-neutral-900/50 px-4 py-2 border-b border-neutral-200 dark:border-neutral-700">
            <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{sectionName}</h4>
          </div>
          <div className="divide-y divide-neutral-100 dark:divide-neutral-700">
            {sectionEntries.map(entry => {
              const ownEntry = overrides[entry.key] || {};
              const visible = effectiveVisible(entry.key);
              const placeholder = inheritedLabel(entry.key);
              const hasOwnOverride = Object.keys(ownEntry).length > 0;

              return (
                <div key={entry.key} className="px-4 py-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setKeyField(entry.key, 'visible', !visible)}
                    title={visible ? 'Currently visible — click to hide' : 'Currently hidden — click to show'}
                    className={`flex items-center justify-center w-8 h-8 rounded ${
                      visible
                        ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30'
                        : 'text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700'
                    }`}
                  >
                    {visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono truncate" title={entry.key}>
                      {entry.key}
                    </div>
                    <div className="mt-1">
                      <input
                        type="text"
                        value={ownEntry.label ?? ''}
                        onChange={(e) => setKeyField(entry.key, 'label', e.target.value || null)}
                        placeholder={placeholder}
                        className="w-full px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-700 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                    {entry.kind === 'section' ? 'Section' : 'Field'}
                  </div>

                  {hasOwnOverride && (
                    <button
                      type="button"
                      onClick={() => clearKey(entry.key)}
                      title={showInheritColumn ? 'Use inherited value' : 'Reset to default'}
                      className="flex items-center justify-center w-8 h-8 rounded text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
